// Field schema for the /login password form (ADR 0009).
//
// Sibling `.form.ts` module, the convention every Form route follows: one module holding
// the Field schema and the form options (including default values), imported by the
// route module. Nothing server-only may be imported here; see `~/lib/forms` for why.
//
// The sign-in pages are the one kind of form with no server action: they submit through
// better-auth's client (docs/contributing/forms.md says why), so this schema gates the
// submission in the browser and better-auth checks the rest. It is validation-only:
// better-auth itself lowercases the email a username is, and refuses one that is not an
// email address (`refusalMessage` in `login.tsx`).
import { formOptions } from "@tanstack/react-form-remix";
import { z } from "zod";
import { requiredText } from "~/lib/forms";

/**
 * What a login attempt must have before it is sent. Note what it does *not* encode:
 * whether the credentials are correct. That is not a property of the input's shape and
 * only better-auth knows it — see `refusalMessage` in `login.tsx`.
 */
export const LoginFieldSchema = z.object({
  username: requiredText("Username is required."),
  password: requiredText("Password is required."),
});

export type LoginFieldValues = z.infer<typeof LoginFieldSchema>;

/**
 * The defaults and the validator, in one place. The Field schema is registered on
 * `onChange`: the SDR is told a field is wrong while typing, and the same validator
 * re-runs on submit (TanStack's default validation logic runs the change validator for
 * the submit cause too).
 */
export const loginFormOptions = formOptions({
  defaultValues: { username: "", password: "" } satisfies LoginFieldValues,
  validators: { onChange: LoginFieldSchema },
});
